import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

const SLIDE_DURATION = 320;

/**
 * A product gallery showing one large stage image with a horizontally scrollable thumbnail
 * strip beneath it. Selecting a thumbnail slides the old stage media out and the new one in.
 *
 * The gallery is bound to the variant picker in both directions. Picking an option moves the
 * stage to that variant's media straight away, ahead of the section re-render that follows, and
 * selecting a thumbnail that belongs to a variant checks that variant in the picker.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} stage - The stage that holds the active media.
 * @property {HTMLElement[]} [stageItem] - Every media node rendered into the stage.
 * @property {HTMLButtonElement[]} [thumb] - The thumbnail buttons.
 *
 * @extends {Component<Refs>}
 */
class CasscadoProductGallery extends Component {
  requiredRefs = ['stage'];

  /** @type {number} */
  #activeIndex = 0;

  /** @type {Animation[]} */
  #animations = [];

  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const initial = Number(this.dataset.initialIndex ?? 0);
    this.#activeIndex = Number.isNaN(initial) ? 0 : initial;
    this.#apply(this.#activeIndex, { animate: false });

    // The picker dispatches on its own element, so the shared section is the closest node that
    // sees both it and this gallery.
    this.closest('.shopify-section, dialog')?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal: this.#controller.signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cancelAnimations();
    this.#controller.abort();
  }

  /**
   * Moves the stage to the media of the variant just picked. The event fires synchronously when
   * an option is clicked, so this runs while the section re-render is still in flight.
   *
   * A variant with no media of its own leaves the stage on the first thumbnail, which is the
   * product's default image.
   *
   * @param {import('@shopify/events').ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const variantId = event.detail?.variantId;
    if (typeof variantId !== 'string' && typeof variantId !== 'number') return;

    const mediaId = this.#variantMedia[String(variantId)];
    const index = mediaId == null ? 0 : this.#indexOfMedia(mediaId);

    this.#show(index === -1 ? 0 : index);
  };

  /**
   * The variant-to-media map the server rendered alongside the gallery.
   * @returns {Record<string, number>}
   */
  get #variantMedia() {
    try {
      return JSON.parse(this.dataset.variantMedia || '{}');
    } catch {
      return {};
    }
  }

  /**
   * @param {number} mediaId - The media id to locate in the stage.
   * @returns {number} Its index, or -1 when that media is not rendered.
   */
  #indexOfMedia(mediaId) {
    return this.#stageItems.findIndex((item) => Number(item.dataset.mediaId) === mediaId);
  }

  /**
   * Shows an index, animating unless it is already current.
   * @param {number} index - The index to show.
   */
  #show(index) {
    if (index === this.#activeIndex) return;

    this.#apply(index, { animate: true, forward: index > this.#activeIndex });
    this.#activeIndex = index;
  }

  /**
   * Re-applies the selection after a section re-render, which happens on every variant change.
   * The server puts the variant's media first, so the stage resets to the first item.
   *
   * The morph can also arrive after a click already moved the stage, in which case the media now
   * sitting first is the one on show and re-applying index 0 is a no-op.
   */
  updatedCallback() {
    this.#activeIndex = 0;
    this.#apply(0, { animate: false });
  }

  /**
   * Selects a thumbnail. Bound through `on:click="/select/<index>"`, which delivers the path
   * segment as the first argument, already coerced to a number.
   *
   * A thumbnail that belongs to a variant also switches the picker to it, which is what makes
   * the strip a second way to choose a pack size.
   *
   * @param {unknown} data - The index encoded in the handler path.
   */
  select(data) {
    const index = typeof data === 'number' ? data : Number(data);

    if (Number.isNaN(index)) return;

    // Showing the media first, then selecting the variant, keeps the stage from moving twice:
    // the picker's event fires synchronously and resolves to this same media, so the handler
    // finds the index already current and does nothing.
    this.#show(index);
    this.#selectVariant(this.#thumbs[index]?.dataset.variantId);
  }

  /**
   * Checks the picker's radio, or selects the option in its dropdown, for a variant. The picker
   * listens for `change`, so this goes through the same path as a click on the option itself and
   * updates price, URL and add-to-cart along with the gallery.
   *
   * @param {string | undefined} variantId - The variant to select.
   */
  #selectVariant(variantId) {
    if (!variantId) return;

    const picker = this.closest('.shopify-section, dialog')?.querySelector('variant-picker');
    if (!picker) return;

    /** @type {HTMLInputElement | HTMLOptionElement | null} */
    const option = picker.querySelector(
      `input[data-variant-id="${variantId}"], option[data-variant-id="${variantId}"]`
    );

    // The picker marks a sold-out option with `aria-disabled` rather than `disabled`, so it stays
    // focusable. Either one means the option is not selectable.
    if (!option || option.disabled || option.getAttribute('aria-disabled') === 'true') return;

    if (option instanceof HTMLInputElement) {
      if (option.checked) return;
      option.checked = true;
      option.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const select = option.closest('select');
    if (!select || select.value === option.value) return;

    select.value = option.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * @param {number} index - The index to show.
   * @param {{animate: boolean, forward?: boolean}} options - Whether to run the slide transition.
   */
  #apply(index, { animate, forward = true }) {
    const items = this.#stageItems;
    const next = items[index];

    if (!next) return;

    // A transition still running owns an outgoing element that is `data-active` but no longer
    // current. Cancelling first, then reading the flag, keeps the two in step.
    this.#cancelAnimations();
    this.#markThumbnails(index);

    const previous = items.find((item) => item !== next && item.hasAttribute('data-active'));

    for (const item of items) {
      if (item !== next && item !== previous) item.removeAttribute('data-active');
    }

    next.setAttribute('data-active', '');

    if (!animate || !previous || this.#prefersReducedMotion) {
      previous?.removeAttribute('data-active');
      this.#pauseVideos(next);
      return;
    }

    const distance = forward ? 1 : -1;
    const timing = { duration: SLIDE_DURATION, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' };

    const outgoing = previous.animate(
      [
        { transform: 'translateX(0)', opacity: 1 },
        { transform: `translateX(${-distance * 100}%)`, opacity: 0 },
      ],
      timing
    );

    const incoming = next.animate(
      [
        { transform: `translateX(${distance * 100}%)`, opacity: 0 },
        { transform: 'translateX(0)', opacity: 1 },
      ],
      timing
    );

    this.#animations = [outgoing, incoming];

    outgoing.finished
      .then(() => previous.removeAttribute('data-active'))
      // A cancelled animation rejects; the next transition then owns the attribute.
      .catch(() => {});

    this.#pauseVideos(next);
  }

  /**
   * Stops any video that is no longer on the stage so audio does not keep playing behind
   * the newly selected media.
   * @param {Element} active - The media node that is now active.
   */
  #pauseVideos(active) {
    for (const video of this.querySelectorAll('video')) {
      if (!active.contains(video)) video.pause();
    }
  }

  /**
   * @param {number} index - The selected index.
   */
  #markThumbnails(index) {
    const thumbs = this.#thumbs;

    for (const [position, thumb] of thumbs.entries()) {
      const selected = position === index;
      thumb.setAttribute('aria-selected', String(selected));
      thumb.toggleAttribute('data-active', selected);
    }

    const active = thumbs[index];
    const strip = active?.parentElement;

    // scrollIntoView would also scroll the page vertically to reach the strip, so the strip's
    // own scroll offset is set directly instead.
    if (active && strip && strip.scrollWidth > strip.clientWidth) {
      const target = active.offsetLeft - (strip.clientWidth - active.clientWidth) / 2;
      strip.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    }
  }

  #cancelAnimations() {
    const [outgoing] = this.#animations;

    for (const animation of this.#animations) animation.cancel();
    this.#animations = [];

    // `cancel()` rejects the `finished` promise, so the outgoing element would keep the flag.
    if (outgoing?.effect instanceof KeyframeEffect) {
      const target = outgoing.effect.target;
      if (target instanceof HTMLElement) target.removeAttribute('data-active');
    }
  }

  /** @returns {HTMLElement[]} */
  get #stageItems() {
    const { stageItem } = this.refs;
    return Array.isArray(stageItem) ? stageItem : stageItem ? [stageItem] : [];
  }

  /** @returns {HTMLButtonElement[]} */
  get #thumbs() {
    const { thumb } = this.refs;
    return Array.isArray(thumb) ? thumb : thumb ? [thumb] : [];
  }

  get #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

if (!customElements.get('casscado-product-gallery')) {
  customElements.define('casscado-product-gallery', CasscadoProductGallery);
}
